import { useState, useEffect } from 'react';
import { ArrowLeft, Save, X, Plus, Trash2, Upload, Loader2 } from 'lucide-react';
import { PLATFORMS, CATEGORIES, upsertStoreProduct, slugify, fileUrl } from '../lib/storeApi';

const empty = {
  title: '', slug: '', brand: '', shortDescription: '', description: '',
  price: '', mrp: '', currency: 'INR', rating: '', reviewCount: '',
  category: '', subCategory: '', tags: [], platform: 'amazon',
  affiliateUrl: '', features: [], specifications: [], highlights: [],
  reviews: [], deliveryInfo: '', returnPolicy: '',
  isActive: true, isFeatured: false, displayOrder: 0,
};

export default function StoreProductForm({ product, onClose, onSaved }) {
  const [form, setForm] = useState(empty);
  const [images, setImages] = useState([]);
  const [existingImages, setExisting] = useState([]);
  const [saving, setSaving] = useState(false);
  const [autoSlug, setAutoSlug] = useState(!product);

  useEffect(() => {
    if (product) {
      setForm({ ...empty, ...product });
      setExisting(product.images || []);
      setAutoSlug(false);
    } else {
      setForm(empty); setExisting([]); setAutoSlug(true);
    }
  }, [product]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const onTitleChange = (v) => { setField('title', v); if (autoSlug) setField('slug', slugify(v)); };
  const onImages = (files) => { const arr = Array.from(files || []); setImages(prev => [...prev, ...arr].slice(0, 8)); };
  const removeNewImage = (i) => setImages(prev => prev.filter((_, idx) => idx !== i));
  const removeExisting = (n) => setExisting(prev => prev.filter(x => x !== n));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.slug || !form.price || !form.affiliateUrl || !form.platform) {
      alert('Fill: title, slug, price, affiliate URL, platform.'); return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      ['title','slug','brand','shortDescription','description','currency','category','subCategory','platform','affiliateUrl','deliveryInfo','returnPolicy'].forEach(k => fd.append(k, form[k] || ''));
      ['price','mrp','rating','reviewCount','displayOrder'].forEach(k => { if (form[k] !== '' && form[k] != null) fd.append(k, String(form[k])); });
      fd.append('isActive', form.isActive ? 'true' : 'false');
      fd.append('isFeatured', form.isFeatured ? 'true' : 'false');
      fd.append('tags', JSON.stringify(form.tags || []));
      fd.append('features', JSON.stringify(form.features || []));
      fd.append('specifications', JSON.stringify(form.specifications || []));
      fd.append('highlights', JSON.stringify(form.highlights || []));
      fd.append('reviews', JSON.stringify(form.reviews || []));
      existingImages.forEach(n => fd.append('images', n));
      images.forEach(f => fd.append('images', f));
      await upsertStoreProduct(product ? product.id : null, fd);
      if (onSaved) onSaved();
    } catch (err) {
      console.error(err);
      alert('Save failed: ' + (err && err.message ? err.message : JSON.stringify(err)));
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200';

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <button onClick={onClose} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-amber-600 mb-4">
        <ArrowLeft size={14} /> Back
      </button>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-5">{product ? 'Edit Product' : 'Add New Product'}</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Section title="Basic Info">
            <Field label="Product Title *">
              <input value={form.title} onChange={e => onTitleChange(e.target.value)} placeholder="e.g. boAt Airdopes 141" className={inputCls} />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Slug * (URL)">
                <div className="flex gap-2">
                  <input value={form.slug} onChange={e => { setField('slug', e.target.value); setAutoSlug(false); }} className={inputCls} />
                  <button type="button" onClick={() => setField('slug', slugify(form.title))} className="px-3 text-xs bg-gray-100 rounded-lg whitespace-nowrap">Auto</button>
                </div>
              </Field>
              <Field label="Brand"><input value={form.brand} onChange={e => setField('brand', e.target.value)} className={inputCls} /></Field>
            </div>
            <Field label="Short Description (max 500 chars)">
              <textarea rows={2} value={form.shortDescription} maxLength={500} onChange={e => setField('shortDescription', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Full Description (HTML supported)">
              <textarea rows={5} value={form.description} onChange={e => setField('description', e.target.value)} className={inputCls + ' font-mono text-xs'} />
            </Field>
          </Section>

          <Section title="Marketplace & Affiliate">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Platform *">
                <select value={form.platform} onChange={e => setField('platform', e.target.value)} className={inputCls}>
                  {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </Field>
              <Field label="Category">
                <select value={form.category} onChange={e => setField('category', e.target.value)} className={inputCls}>
                  <option value="">— Select —</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Affiliate URL * (direct product link)">
              <input type="url" value={form.affiliateUrl} onChange={e => setField('affiliateUrl', e.target.value)} placeholder="https://www.amazon.in/dp/..." className={inputCls} />
            </Field>
          </Section>

          <Section title="Pricing & Ratings">
            <div className="grid sm:grid-cols-4 gap-4">
              <Field label="Price * (₹)"><input type="number" value={form.price} onChange={e => setField('price', e.target.value)} className={inputCls} /></Field>
              <Field label="MRP (₹)"><input type="number" value={form.mrp} onChange={e => setField('mrp', e.target.value)} className={inputCls} /></Field>
              <Field label="Rating (0–5)"><input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={e => setField('rating', e.target.value)} className={inputCls} /></Field>
              <Field label="Review Count"><input type="number" value={form.reviewCount} onChange={e => setField('reviewCount', e.target.value)} className={inputCls} /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Delivery Info"><input value={form.deliveryInfo} onChange={e => setField('deliveryInfo', e.target.value)} placeholder="Free delivery 2–4 days" className={inputCls} /></Field>
              <Field label="Return Policy"><input value={form.returnPolicy} onChange={e => setField('returnPolicy', e.target.value)} placeholder="7-day replacement" className={inputCls} /></Field>
            </div>
          </Section>

          <Section title="Product Images (max 8, first = cover)">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {existingImages.map(n => (
                <div key={n} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                  <img src={fileUrl(product, n)} alt="" className="w-full h-full object-contain p-1" />
                  <button type="button" onClick={() => removeExisting(n)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5"><X size={11} /></button>
                </div>
              ))}
              {images.map((f, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-amber-300 bg-amber-50">
                  <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-contain p-1" />
                  <button type="button" onClick={() => removeNewImage(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5"><X size={11} /></button>
                </div>
              ))}
              {existingImages.length + images.length < 8 && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-amber-400 hover:text-amber-500 cursor-pointer">
                  <Upload size={18} /><span className="text-[10px] mt-1 font-medium">Upload</span>
                  <input type="file" accept="image/*" multiple hidden onChange={e => onImages(e.target.files)} />
                </label>
              )}
            </div>
          </Section>

          <Section title="Key Features / Highlights">
            <JSONListField items={form.features} onChange={v => setField('features', v)} placeholder="e.g. Up to 42 hours playback" addLabel="Add feature" inputCls={inputCls} />
          </Section>
          <Section title="Specifications (key–value)">
            <KVField items={form.specifications} onChange={v => setField('specifications', v)} inputCls={inputCls} />
          </Section>
          <Section title="Sample Reviews (optional)">
            <ReviewField items={form.reviews} onChange={v => setField('reviews', v)} inputCls={inputCls} />
          </Section>
          <Section title="Visibility">
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isActive} onChange={e => setField('isActive', e.target.checked)} /><span>Active</span></label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isFeatured} onChange={e => setField('isFeatured', e.target.checked)} /><span>⭐ Featured</span></label>
              <label className="flex items-center gap-2 text-sm">Order: <input type="number" value={form.displayOrder} onChange={e => setField('displayOrder', e.target.value)} className="w-20 px-2 py-1 border rounded" /></label>
            </div>
          </Section>
          <div className="flex justify-end gap-3 border-t pt-5">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-xl">
              {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : <><Save size={14} /> Save</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }) { return <div><h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">{title}</h3><div className="space-y-4">{children}</div></div>; }
function Field({ label, children }) { return <label className="block"><span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>{children}</label>; }
function JSONListField({ items, onChange, placeholder, addLabel, inputCls }) {
  items = items || [];
  return (
    <div className="space-y-2">
      {items.map((v, i) => (
        <div key={i} className="flex gap-2">
          <input value={v} onChange={e => onChange(items.map((x, idx) => idx === i ? e.target.value : x))} placeholder={placeholder} className={inputCls} />
          <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, ''])} className="inline-flex items-center gap-1 text-xs font-medium text-amber-600"><Plus size={12} /> {addLabel || 'Add'}</button>
    </div>
  );
}
function KVField({ items, onChange, inputCls }) {
  items = items || [];
  return (
    <div className="space-y-2">
      {items.map((row, i) => (
        <div key={i} className="flex gap-2">
          <input value={row.key} onChange={e => onChange(items.map((x, idx) => idx === i ? { ...x, key: e.target.value } : x))} placeholder="Key" className={inputCls + ' w-1/3'} />
          <input value={row.value} onChange={e => onChange(items.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x))} placeholder="Value" className={inputCls + ' flex-1'} />
          <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, { key: '', value: '' }])} className="inline-flex items-center gap-1 text-xs font-medium text-amber-600"><Plus size={12} /> Add specification</button>
    </div>
  );
}
function ReviewField({ items, onChange, inputCls }) {
  items = items || [];
  return (
    <div className="space-y-3">
      {items.map((r, i) => (
        <div key={i} className="border border-gray-100 rounded-xl p-3 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <input value={r.name} onChange={e => onChange(items.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} placeholder="Reviewer" className={inputCls} />
            <input type="number" step="0.1" min="0" max="5" value={r.rating} onChange={e => onChange(items.map((x, idx) => idx === i ? { ...x, rating: +e.target.value } : x))} className={inputCls} />
            <input value={r.date} onChange={e => onChange(items.map((x, idx) => idx === i ? { ...x, date: e.target.value } : x))} placeholder="Date" className={inputCls} />
          </div>
          <textarea value={r.comment} onChange={e => onChange(items.map((x, idx) => idx === i ? { ...x, comment: e.target.value } : x))} placeholder="Review..." rows={2} className={inputCls} />
          <div className="text-right"><button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-xs text-red-500 hover:underline">Remove</button></div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, { name: '', rating: 5, comment: '', date: '' }])} className="inline-flex items-center gap-1 text-xs font-medium text-amber-600"><Plus size={12} /> Add review</button>
    </div>
  );
}
